// /reports/gst — GST Report (PAGES.md §3.41)

import { useState, useMemo } from 'react';
import type { ColumnDef, TimeRangeValue, TaxRegimePresentation } from '@bbpos/ui';
import { formatPaise } from '@/utils/currency';
import { getSubunitMultiplier } from '@bbpos/utils';
import type { CurrencyCode } from '@bbpos/types';
import { useTaxRegime } from '@/hooks/use-tax-regime';
import {
  ReportPageShell,
  ReportFilterBar,
  ReportKpiRow,
  ReportTable,
  ReportExportMenu,
  ReportSkeleton,
  ReportError,
  useReportFilter,
  useReportQuery,
  reportsApi,
  downloadCsv,
  shareReportLink,
  DEFAULT_REPORT_PRESET,
  KpiIcons,
} from '@/features/reports';
import type { GstSlabRow } from '@/features/reports';
import { useTenantCurrency, getCurrencySymbol } from '@/hooks/use-tenant-currency';

type Row = GstSlabRow & { id: string };

function makeColumns(currency: CurrencyCode, tax: TaxRegimePresentation): ColumnDef<Row>[] {
  // India (GST_INDIA) splits per line into CGST/SGST/IGST; every GCC regime is a
  // single VAT line (no inter/intra split). `splitLabels` is the regime gate —
  // INR keeps the exact three-column layout, GCC collapses to one `taxName`
  // column whose value is the consolidated line tax.
  const taxColumns: ColumnDef<Row>[] = tax.splitLabels
    ? [
        { key: 'cgst', header: tax.splitLabels[0], numeric: true, render: (_v, row) => <span className="tabular-nums">{formatPaise(row.cgst, currency)}</span> },
        { key: 'sgst', header: tax.splitLabels[1], numeric: true, render: (_v, row) => <span className="tabular-nums">{formatPaise(row.sgst, currency)}</span> },
        { key: 'igst', header: tax.splitLabels[2], numeric: true, render: (_v, row) => <span className="tabular-nums">{formatPaise(row.igst, currency)}</span> },
        { key: 'total_tax', header: 'Total Tax', numeric: true, render: (_v, row) => <span className="text-sm font-semibold tabular-nums">{formatPaise(row.total_tax, currency)}</span> },
      ]
    : [
        { key: 'total_tax', header: tax.taxName, numeric: true, render: (_v, row) => <span className="text-sm font-semibold tabular-nums">{formatPaise(row.total_tax, currency)}</span> },
      ];
  return [
    { key: 'slab', header: 'Tax Slab', render: (_v, row) => <span className="inline-flex items-center rounded-full bg-gradient-to-r from-teal-500 to-success-500 px-3 py-1 text-xs font-bold text-white">{row.slab}</span> },
    { key: 'taxable_amount', header: 'Taxable', numeric: true, render: (_v, row) => <span className="tabular-nums">{formatPaise(row.taxable_amount, currency)}</span> },
    ...taxColumns,
  ];
}

export function GstReportPage() {
  const [timeRange, setTimeRange] = useState<TimeRangeValue>(DEFAULT_REPORT_PRESET);
  const [locationId, setLocationId] = useState<string | undefined>(undefined);
  const filter = useReportFilter(timeRange, locationId);
  const currency = useTenantCurrency();
  const tax = useTaxRegime();
  const symbol = getCurrencySymbol(currency);
  const subunit = getSubunitMultiplier(currency);
  const csvDecimals = Math.round(Math.log10(subunit));
  const columns = useMemo(() => makeColumns(currency, tax), [currency, tax]);

  const { data, isLoading, error } = useReportQuery({
    key: 'gst',
    filter,
    queryFn: (f) => reportsApi.getGstSummary(f),
  });

  const rows: Row[] = useMemo(
    () => (data?.slabs ?? []).map((s) => ({ ...s, id: s.slab })),
    [data],
  );

  const handleExport = (fmt: 'csv' | 'pdf' | 'whatsapp' | 'email') => {
    if (fmt === 'pdf') {
      // Browser print dialog -> "Save as PDF"; no server-side PDF renderer exists.
      window.print();
      return;
    }
    if (fmt === 'csv') {
      const splitColumns = tax.splitLabels
        ? [
            { header: `${tax.splitLabels[0]} (${symbol})`, get: (r: Row) => (r.cgst / subunit).toFixed(csvDecimals) },
            { header: `${tax.splitLabels[1]} (${symbol})`, get: (r: Row) => (r.sgst / subunit).toFixed(csvDecimals) },
            { header: `${tax.splitLabels[2]} (${symbol})`, get: (r: Row) => (r.igst / subunit).toFixed(csvDecimals) },
          ]
        : [{ header: `${tax.taxName} (${symbol})`, get: (r: Row) => (r.total_tax / subunit).toFixed(csvDecimals) }];
      downloadCsv(
        `${tax.taxName.toLowerCase()}-report-${filter.range.from}-to-${filter.range.to}.csv`,
        [
          { header: 'Slab', get: (r: Row) => r.slab },
          { header: `Taxable (${symbol})`, get: (r: Row) => (r.taxable_amount / subunit).toFixed(csvDecimals) },
          ...splitColumns,
          { header: `Total (${symbol})`, get: (r: Row) => (r.total_tax / subunit).toFixed(csvDecimals) },
        ],
        rows,
      );
      return;
    }
    if (fmt === 'whatsapp' || fmt === 'email') {
      shareReportLink(fmt, `Orio360 ${tax.taxName} Report`, `Total Tax: ${formatPaise(data?.total_tax ?? 0, currency)} (${filter.range.from} → ${filter.range.to})`);
    }
  };

  return (
    <ReportPageShell
      title={`${tax.taxName} Report`}
      subtitle={
        tax.splitLabels
          ? 'Tax collected by slab — CGST, SGST and IGST breakdown for the filing period.'
          : `Tax collected by slab — ${tax.taxName} breakdown for the filing period.`
      }
      breadcrumbs={[{ label: 'Home', href: '/home' }, { label: 'Reports', href: '/reports' }, { label: tax.taxName }]}
      accent="teal-success"
      headerActions={<ReportExportMenu onExport={handleExport} disabled={!rows.length} />}
      filterBar={<ReportFilterBar timeRange={timeRange} onTimeRangeChange={setTimeRange} locationId={locationId} onLocationChange={setLocationId} />}
    >
      {isLoading ? (
        <ReportSkeleton kpiCount={4} />
      ) : error ? (
        <ReportError />
      ) : (
        <>
          <ReportKpiRow
            tiles={
              tax.splitLabels
                ? [
                    { id: 'total', title: 'Total Tax Collected', value: formatPaise(data?.total_tax ?? 0, currency), tone: 'indigo', icon: KpiIcons.rupee },
                    { id: 'cgst', title: tax.splitLabels[0], value: formatPaise(data?.total_cgst ?? 0, currency), tone: 'brand', icon: KpiIcons.percent },
                    { id: 'sgst', title: tax.splitLabels[1], value: formatPaise(data?.total_sgst ?? 0, currency), tone: 'violet', icon: KpiIcons.percent },
                    { id: 'igst', title: tax.splitLabels[2], value: formatPaise(data?.total_igst ?? 0, currency), tone: 'teal', icon: KpiIcons.percent },
                  ]
                : [
                    { id: 'total', title: `Total ${tax.taxName} Collected`, value: formatPaise(data?.total_tax ?? 0, currency), tone: 'indigo', icon: KpiIcons.rupee },
                  ]
            }
          />
          <ReportTable
            title={`${tax.taxName} Breakdown by Slab`}
            columns={columns}
            data={rows}
            emptyTitle={`No ${tax.taxName} data for selected period`}
            emptyDescription="Complete a few sales to see tax slab totals."
            totalCount={rows.length}
          />
        </>
      )}
    </ReportPageShell>
  );
}
